// Usage + billing data layer. Reads `GET /v1/billing/usage` — the per-window
// usage rollup (handled units, model spend vs cap) the dashboard's Usage view
// renders. Read-only; mirrors `lib/onboarding.ts` (round-trip over `apiFetch`
// reusing the dev session bearer). `agent_hours` backs the free tier's agent-hours band (arc 101 slice 01).

import {
  MAX_CHECKOUT_SEATS,
  Scope,
  SELF_SERVE_SEAT_TIERS,
  type SelfServeSeatTier,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { ApiError, apiFetch, errorReason, isForbidden, isUnauthorized } from './api';
import { roleSettled } from './roles';
import { useScopeVerdict } from './scope-gate';
import { SESSION_TOKEN, useMe } from './session';

/** Spend state for the window — escalates as usage approaches the cap (#156). */
export type SpendThreshold = 'ok' | 'warn_50' | 'warn_80' | 'capped';

/**
 * The free tier's agent-HOURS ceiling for this window (arc 101 slice 01) — the
 * one the dashboard renders, because the ceiling bites at DISPATCH: a maintainer
 * whose runs stop has no other place to see why.
 *
 * Both numbers are HOURS. The server meters seconds and converts once
 * (`SECONDS_PER_AGENT_HOUR`, `@developerz/billing`); this bundle can't import
 * that package, and a browser-side `/ 3600` is the drift the constant exists to
 * prevent. Hours burned on a DONATED box are absent by construction — fleet work
 * lives in `tasks`, which the meter never joins.
 */
export type AgentHours = {
  /** Hours burned this window — fractional. */
  readonly used_hours: number;
  /** The tier's ceiling in hours; first-month accounts get the tighter one. */
  readonly cap_hours: number;
  /** ISO instant the window rolls and the ceiling self-resumes (next UTC month). */
  readonly resets_at: string;
};

/** Per-window usage rollup — the snake_case shape `GET /v1/billing/usage` returns. */
export type Usage = {
  readonly window: string;
  readonly handled_units: number;
  readonly waived: boolean;
  /**
   * PRICED BYOK model spend for the window, USD (#2057): recorded cost plus
   * stored list price. Read this for the money; never `used_fraction ×
   * spend_cap_usd`, which is 0 for an account with no cap.
   */
  readonly spend_usd: number;
  /**
   * The fail-safe ceiling for the runs the platform could not price: `usd` is an
   * upper bound, never money spent, over `runs` runs. `used_fraction` and
   * `threshold` measure `spend_usd` PLUS it against the cap, because that is what
   * the spend gate refuses on. Absent from an API that predates the field (a tab
   * open across a deploy), which reads as no estimate.
   */
  readonly spend_estimate?: { readonly usd: number; readonly runs: number };
  /**
   * The monthly budget the account set, or `null` when it set none — the same
   * "present-and-null when no ceiling applies" shape as {@link agent_hours}.
   * There is no platform default cap; an account with `null` here is not gated
   * on spend.
   */
  readonly spend_cap_usd: number | null;
  readonly used_fraction: number;
  readonly threshold: SpendThreshold;
  /** Billing is paused (a Stripe payment failed) → the agent is stopped until it's fixed (#230/#235). */
  readonly billing_paused: boolean;
  /**
   * The agent-hours band, or nothing to show. THREE shapes mean one thing here:
   * an object (a ceiling applies), `null` (the server looked and there is none —
   * an uncapped tier), and absent (an API that predates the field, e.g. a tab
   * open across a deploy). All three collapse to "render the band or don't" —
   * the SPA never re-derives who is capped.
   */
  readonly agent_hours?: AgentHours | null;
};

/**
 * Open the Stripe billing portal and send the browser there (#235) — the fix
 * path from the paused banner, where the owner updates a failed card. POST mints
 * a one-time session URL. Throws `ApiError` on non-2xx (e.g. 409
 * `billing-no-customer`, 503 when Stripe isn't configured); the caller surfaces
 * it through {@link portalErrorCopy}.
 */
export async function openBillingPortal(token: string | null = SESSION_TOKEN): Promise<void> {
  const { url } = await apiFetch<{ url: string }>('/v1/billing/portal', token, { method: 'POST' });
  window.location.assign(url);
}

/**
 * The toast for a failed {@link openBillingPortal}.
 *
 * A 409 is `billing-no-customer` (#4772): the account has no Stripe customer, so
 * the Portal has nothing to manage and a retry is refused identically — the way
 * out is buying seats, which creates the customer. Framed by STATUS, not by the
 * problem `type`, because `ApiError` never carries `type` (a standard RFC 9457
 * member `problemExtensions` filters out; the `lib/task-refusal.ts` rule). The
 * status is unambiguous here: `billingNoCustomer` is the only 409 in the API's
 * `ERROR_TYPES` catalog listing the `billing.portal` operation.
 *
 * A 403 is an ANSWER — the caller is missing `write:billing` — and the API's
 * own `detail` names it ("missing scope: write:billing"), so that sentence
 * outranks a client-minted guess (`errorReason`, the seam every inline refusal
 * uses); a 403 with no detail falls back to `errors.denied`. Everything else —
 * a 500's wire `detail` included — keeps the generic try-again line.
 */
export function portalErrorCopy(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) return t('billing.portal_no_customer');
  if (error instanceof ApiError && error.status === 403)
    return errorReason(error, t('errors.denied'));
  return t('billing.portal_error');
}

/**
 * Set the account's monthly spend cap (#237). PATCH the new value; the caller
 * refetches usage to pick up the new (effective) cap + gauge. Throws `ApiError`
 * on non-2xx (e.g. 400 for a non-positive or over-max value).
 */
export async function setSpendCap(
  capUsd: number,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<{ spend_cap_usd: number }>('/v1/billing/cap', token, {
    method: 'PATCH',
    body: JSON.stringify({ spend_cap_usd: capUsd }),
  });
}

export const USAGE_QUERY_KEY = 'account_usage';

/** The caller's usage rollup for the current window. Idle when there's no session. */
export function useUsage(token: string | null = SESSION_TOKEN) {
  return createQuery<Usage>(() => ({
    queryKey: [USAGE_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<Usage>('/v1/billing/usage', token),
  }));
}

// ─── Entitlements (seat billing) ──────────────────────────────────────────────

/**
 * Derived entitlements for an account — what its tier + seat count is entitled
 * to (arc 101 slice 01). Snake_case mirrors the `GET /v1/billing/entitlements` shape.
 */
export type EntitlementInfo = {
  /**
   * Concurrent AI-dev slots the account is entitled to — NOT a purchase count
   * (`seats` above is that): a fleet-bearing `boxes` grant induces one on a free
   * account. `0` = no fleet; {@link UNLIMITED_AI_DEVS} = unlimited (internal
   * dogfood orgs).
   */
  readonly ai_devs: number;
  readonly ci_boxes_per_dev: number;
  readonly review_enabled: boolean;
  readonly queues: 'own' | 'oss';
  /**
   * Total BYOVM boxes the account may enroll — the server's `enrollmentBoxBudget`,
   * the SAME number the enrollment 402 refuses against. Never derived here
   * (`ai_devs × ci_boxes_per_dev` is a CI ratio, and misses grants).
   * {@link UNLIMITED_AI_DEVS} on an `unlimited_fleet` grant — the same
   * `jsonSafeCount` sentinel as `ai_devs`.
   */
  readonly enrollment_box_budget: number;
};

/**
 * Seat-billing snapshot — tier, paid seats, live seat usage, and derived
 * entitlements. Read-only; changes go through the Stripe Portal link.
 */
export type SeatInfo = {
  readonly tier: string;
  /**
   * The tier the platform SERVES the account as (`servedTierOf`,
   * `@developerz/billing`): equal to `tier`, except an account an operator comped
   * a seat reads `tier: 'free'` and `served_tier: 'solo'`. Optional because an
   * older API predates it — absent reads as "not comped".
   */
  readonly served_tier?: string;
  readonly seats: number;
  readonly used_seats: number;
  /**
   * Boxes holding an enrollment slot now — live rows plus pending enrollments
   * whose join token is unexpired. The "active" of the enrollment 402 and of
   * `box add`, compared against `entitlements.enrollment_box_budget`. NOT
   * `used_seats`, which counts online ai-dev runners against paid seats.
   */
  readonly active_boxes: number;
  readonly entitlements: EntitlementInfo;
  /**
   * True when BOTH self-serve price ids are provisioned on the deployment —
   * the same env fact the server's 503 (`price_not_configured`) gates on, just
   * projected as a wire field. The SPA uses it to hide a Buy-seats CTA that
   * would 503 if the operator clicked it; the dashboard reads env vars via the
   * server, never directly. Defaults to `false` when the API predates the
   * field — older clients see the cart shut and fail closed, which is the safe
   * direction (a wrongly-shown CTA costs a 503 toast, a wrongly-hidden one
   * costs the account its only self-serve path).
   */
  readonly seats_checkout_configured: boolean;
};

export const ENTITLEMENTS_QUERY_KEY = 'account_entitlements';

/** The caller's seat-billing entitlements. Idle when there's no session. */
export function useEntitlements(token: string | null = SESSION_TOKEN) {
  return createQuery<SeatInfo>(() => ({
    queryKey: [ENTITLEMENTS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<SeatInfo>('/v1/billing/entitlements', token),
  }));
}

// ─── Seat checkout: the FIRST subscription (arc 101 slice 07) ────────────────

/**
 * Tiers an account may buy self-serve — the domain's `SELF_SERVE_SEAT_TIERS`
 * (`packages/domain/src/entities/account.ts`), the only tiers
 * `POST /v1/billing/checkout` accepts. `enterprise` is a custom contract with no
 * price object, `oss_donor` rides the donation flow, `free`/`oss_verified` never bill.
 */
export const SELF_SERVE_TIERS = SELF_SERVE_SEAT_TIERS;
export type SelfServeTier = SelfServeSeatTier;

/**
 * Seat ceiling for one cart — the domain's `MAX_CHECKOUT_SEATS`, the same constant
 * the route's `CheckoutBodySchema` cites. Re-exported so the seat input reads it
 * from this module.
 */
export { MAX_CHECKOUT_SEATS };

/**
 * Mint a hosted Stripe Checkout session for `quantity` seats of `tier` and send
 * the browser to it. This is the ONLY self-serve path to a FIRST subscription —
 * the Portal (`openBillingPortal`) can just *modify* one that already exists, so
 * without this a fresh team can't go from install to paid at all.
 *
 * Throws `ApiError` on non-2xx (503 when Stripe or the tier's price isn't
 * provisioned, 403 without `write:billing`, 400 on a rejected cart); the caller
 * surfaces it. Seats land on the account only when Stripe's
 * `customer.subscription.created` webhook reaches the worker's seat-sync
 * consumer — a returned URL is an intent to buy, never a grant.
 *
 * `attemptKey` names ONE buy attempt — the caller mints it per click
 * (`crypto.randomUUID()`, `SeatCheckout`) and resends it on that attempt's
 * retries. It rides as `Idempotency-Key`, which the API scopes the Stripe
 * session to (plan 101 06-7): the same key answers with the same hosted
 * session, and a request without one is a fresh attempt — a second session.
 */
export async function startSeatCheckout(
  input: { tier: SelfServeTier; quantity: number },
  token: string | null,
  attemptKey: string,
): Promise<void> {
  const { url } = await apiFetch<{ url: string }>('/v1/billing/checkout', token, {
    method: 'POST',
    headers: { 'Idempotency-Key': attemptKey },
    body: JSON.stringify({ tier: input.tier, quantity: input.quantity }),
  });
  window.location.assign(url);
}

/**
 * Wire sentinel for an UNLIMITED `ai_devs` count. `@developerz/billing` resolves
 * an `unlimited_fleet` grant to `Infinity`, which `JSON.stringify` would emit as
 * `null`, so `GET /v1/billing/entitlements` sends `-1` instead (`UNLIMITED` in
 * `apps/api/src/routes/billing.ts`). Named here because the dashboard cannot
 * import an app, and a bare `-1` in a predicate is a magic number.
 */
export const UNLIMITED_AI_DEVS = -1;

/**
 * True when no seat plan is in force, i.e. Checkout (not the Portal) is the way
 * in. Read off `seats` — the paid SUBSCRIPTION quantity — plus the unlimited
 * sentinel, and deliberately NOT off `ai_devs`.
 *
 * `ai_devs` used to answer this alone, and stopped when a `boxes` grant became
 * fleet-bearing on a non-fleet tier (arc 101 slice 03,
 * `packages/billing/src/tiers.ts`): a granted free account resolves to
 * `ai_devs: 1` with `seats: 0` and no subscription, so the capacity read would
 * hide the seat CTA from exactly the account shape that arc creates — leaving it
 * with a "Manage subscription" button that 409s and no way to ever buy a second
 * ai-dev. `seats` is the field that means "bought", and it is the question.
 *
 * `0` = never subscribed, or a paid tier drained to zero seats (both need a NEW
 * subscription, which is Checkout); `> 0` = seats already bought (the Portal owns
 * changes); {@link UNLIMITED_AI_DEVS} = the internal-dogfood override, never sold
 * to whatever its seat count says.
 */
export const needsSeatPlan = (seat: SeatInfo): boolean =>
  seat.seats === 0 && seat.entitlements.ai_devs !== UNLIMITED_AI_DEVS && !isComped(seat);

/**
 * An operator comped this account a seat (an `ai_devs` / `unlimited_fleet` grant):
 * it is served as a seat tier while its billing tier says otherwise. Such an
 * account is not sold a subscription — nothing nags it to buy what it was given.
 */
export const isComped = (seat: SeatInfo): boolean =>
  seat.served_tier !== undefined && seat.served_tier !== seat.tier;

/**
 * The buy-seats gate: plan state × the settled `write:billing` verdict ×
 * deployment config. One
 * predicate, three consumers (the Billing panel, the onboarding hand-off card,
 * and the funnel rail's conditional seat step — `onboardingStepLabels`), so no
 * surface can drift on who gets offered a subscription. In particular the rail
 * can't advertise a step whose card the same gate hides.
 *
 * The verdict (`useScopeVerdict`, `lib/scope-gate.ts`) reads the scope list
 * `/v1/me` publishes — the authority — not the role table: on the surfaces this
 * change moved (the seat offer, the two portal buttons, the cap editor, the
 * OSS apply form) `role` no longer answers who may write billing — a cap
 * narrows a role's ceiling, and a PAT carries none. Two `write:billing`
 * mirrors are still on the role ladder, pending their own move: the Agents
 * model pin (`Agents.tsx`) and the org-key priority editor (`AccountKeys.tsx`
 * / `key-capacity.ts`) keep gating on `roleSettled(me) && canAdminWrite(role)`.
 * Both `denied` and `unsettled` hide the CTA — no button that flips in a
 * moment, no 403 the buyer was set up to earn; `allowed` also covers a settled
 * `/v1/me` that publishes no list (legacy answer — the server stays the
 * arbiter).
 *
 * The third clause (`seats_checkout_configured`) closes the gate on a
 * deployment that has Stripe but neither self-serve price id provisioned —
 * the same env fact the server's 503 (`price_not_configured`) gates on, sent
 * as a wire field. ANDing the flag here keeps the cart hidden until BOTH
 * prices are provisioned; otherwise the CTA would render and 503 on click,
 * which is the worst of both worlds. Absent data defaults to "gate shut"
 * (older API → fail closed).
 */
export function useSeatOffer(token: string | null = SESSION_TOKEN): () => boolean {
  const seats = useEntitlements(token);
  const writeAllowed = useScopeVerdict(Scope.WriteBilling, token);
  return () => {
    const seat = seats.data;
    return (
      seat !== undefined &&
      seat.seats_checkout_configured === true &&
      needsSeatPlan(seat) &&
      writeAllowed() === 'allowed'
    );
  };
}

/**
 * Whether {@link useSeatOffer} has an ANSWER yet — both of its inputs resolved,
 * either way. Lives here, over the same two queries, so "what feeds the gate"
 * stays one fact in one file.
 *
 * The funnel rail is the consumer. `/v1/billing/entitlements` resolves after
 * `/v1/onboarding/state`, so a rail painted the moment the funnel state lands
 * reads "One-time setup. 7 steps." and then re-renders to 8 for every unpaid
 * account — the count changing under the reader on the screen whose whole job
 * is telling them how much is left. An error counts as settled: the verdict's
 * own `unsettled` already hides the CTA a failed read cannot authorize, and
 * waiting forever would trade a flicker for a rail that never appears. The
 * me-half is the verdict's own settlement — {@link roleSettled} (answered, and
 * not a carried-over placeholder from the previous org) — with an error still
 * counting as answered.
 */
export function useSeatOfferSettled(token: string | null = SESSION_TOKEN): () => boolean {
  const seats = useEntitlements(token);
  const me = useMe(token);
  return () => (seats.data !== undefined || seats.isError) && (roleSettled(me) || me.isError);
}

/**
 * What a failed `GET /v1/billing/usage` SAYS. Lives beside {@link useUsage}
 * because it is a fact about that read, not about the page that renders it —
 * and because `<Billing/>` hand-rolls its fallback instead of going through
 * `<QueryBoundary/>`, so it is one of the route-owned error paths the boundary
 * can never reach.
 *
 * Three answers, and the split is the credential:
 *
 *   - a 403 is an ANSWER — the caller is missing `read:billing`, or the tenant
 *     claim was refused, and the re-ask carries the same credential — so the
 *     API's own `detail` is the sentence (`errorReason`, the seam every inline
 *     refusal already uses) over a client-minted guess at why. The route drops
 *     the Retry alongside this, via `isForbidden`;
 *   - a 401 returns `undefined`, delegating to `<ErrorState/>`'s own 401 copy,
 *     which is the sign-in phrasing and IS actionable;
 *   - anything else keeps `billing.error`. A 500's `detail` is wire debug;
 *     "Couldn't load usage" says more.
 */
export function usageErrorCopy(error: unknown): string | undefined {
  if (isForbidden(error)) return errorReason(error, t('errors.denied'));
  if (isUnauthorized(error)) return undefined;
  return t('billing.error');
}
